import { fetchJSON as fetchAPI } from './client';

export type BusinessObjective = { key: string; name: string; description: string; kpi?: string };

export type CapabilityModule = { key: string; name: string; description?: string; integrations?: string[]; order: number };

export type HealthScoreComponent = {
  key: string; name: string; current_value?: string; target?: string; gap?: string;
  priority?: string; business_impact?: string; ai_explanation?: string;
  evidence?: string[]; recommended_actions?: string[]; weight?: number;
};

export type IntelligenceEngine = {
  key: string; name: string; section?: string; analysis_dimensions?: string[];
  outputs?: string[]; description?: string;
};

export type ListingRecommendation = {
  key: string; title: string; business_objective: string; evidence?: string[];
  expected_ctr_improvement: string; expected_cvr_improvement: string;
  expected_revenue: string; expected_profit: string; confidence: number;
  effort: string; risk: string; observation_window: string;
  experiment_link?: string; rollback_guidance?: string; category?: string;
};

export type ExperimentIntegrationStep = { key: string; name: string; order: number; description?: string; automated: boolean };

export type UXCapability = { key: string; name: string; description?: string; mode?: string };

export type PerformanceTarget = { key: string; surface: string; max_value: string; unit: string; description?: string };

export type APISpecification = {
  key: string; category: string; endpoints?: string[]; description: string;
  authorization?: string; rate_limit?: string; cache_strategy?: string;
};

export type DatabaseTable = {
  key: string; name: string; purpose: string; columns?: string[]; indexes?: string[]; relationships?: string[];
};

export type TestingRequirement = { key: string; level: string; scope: string; required: boolean; description?: string };

export type AcceptanceCriterion = { key: string; criterion: string; measurable: boolean; target?: string };

export type EngineValidation = {
  valid: boolean; health_engine_ready: boolean; intelligence_ready: boolean;
  recommendations_ready: boolean; experiment_integration: boolean;
  performance_met: boolean; acceptance_passed: boolean;
  gaps?: string[]; evaluated_at: string;
};

export type EngineDashboard = {
  prd_id: string; tenant_id: string; version: string; depends_on: string[];
  business_objectives: number; capability_modules: number; health_score_components: number;
  intelligence_engines: number; recommendations: number; experiment_steps: number;
  ux_capabilities: number; performance_targets: number; api_specifications: number;
  database_tables: number; testing_requirements: number; acceptance_criteria: number;
  generated_at: string;
};

export type LGEPRDStatus = {
  prd_id: string; version: string; depends_on: string[];
  business_objectives: number; capability_modules: number; health_score_components: number;
  intelligence_engines: number; recommendations: number; experiment_steps: number;
  ux_capabilities: number; performance_targets: number; api_specifications: number;
  database_tables: number; testing_requirements: number; acceptance_criteria: number;
  evidence_based: boolean; production_ready: boolean; status: string;
};

const MARKETINTEL_BASE = import.meta.env.VITE_MARKETINTEL_API ?? '/api/v1/marketintel';

function fetchMarketIntel<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${MARKETINTEL_BASE}${path}`, init);
}

export const listingGrowthEngineApi = {
  dashboard: () => fetchMarketIntel<EngineDashboard>('/lge/dashboard'),
  businessObjectives: () => fetchMarketIntel<{ objectives: BusinessObjective[] }>('/lge/business-objectives').then((r) => r.objectives),
  capabilityArchitecture: () => fetchMarketIntel<{ capabilities: CapabilityModule[] }>('/lge/capability-architecture').then((r) => r.capabilities),
  healthEngine: () => fetchMarketIntel<{ components: HealthScoreComponent[] }>('/lge/health-engine').then((r) => r.components),
  intelligenceEngines: () => fetchMarketIntel<{ engines: IntelligenceEngine[] }>('/lge/intelligence-engines').then((r) => r.engines),
  recommendations: () => fetchMarketIntel<{ recommendations: ListingRecommendation[] }>('/lge/recommendations').then((r) => r.recommendations),
  experimentIntegration: () => fetchMarketIntel<{ steps: ExperimentIntegrationStep[] }>('/lge/experiment-integration').then((r) => r.steps),
  uxCapabilities: () => fetchMarketIntel<{ capabilities: UXCapability[] }>('/lge/ux-capabilities').then((r) => r.capabilities),
  performanceTargets: () => fetchMarketIntel<{ targets: PerformanceTarget[] }>('/lge/performance-targets').then((r) => r.targets),
  apis: () => fetchMarketIntel<{ apis: APISpecification[] }>('/lge/apis').then((r) => r.apis),
  databaseSchema: () => fetchMarketIntel<{ tables: DatabaseTable[] }>('/lge/database-schema').then((r) => r.tables),
  testing: () => fetchMarketIntel<{ requirements: TestingRequirement[] }>('/lge/testing').then((r) => r.requirements),
  acceptanceCriteria: () => fetchMarketIntel<{ criteria: AcceptanceCriterion[] }>('/lge/acceptance-criteria').then((r) => r.criteria),
  validate: (payload: { attrs: Record<string, boolean> }) =>
    fetchMarketIntel<EngineValidation>('/lge/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  status: () => fetchMarketIntel<LGEPRDStatus>('/lge/status'),
};
